import posthog from "posthog-js";

let _userId: string | null = null;

function sendToApi(endpoint: string, payload: Record<string, unknown>) {
  try {
    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Never let analytics break the app
  }
}

/**
 * Set the current user ID for analytics tracking.
 * Called from providers.tsx when auth state changes.
 */
export function setAnalyticsUser(userId: string | null) {
  _userId = userId;
}

type TrackOptions = {
  /**
   * Ueberschreibt die `user_id` fuer genau dieses Ereignis.
   *
   * Noetig, wo das Ereignis in derselben Runde feuert, in der die Anmeldung
   * geschieht: `_userId` wird von `setAnalyticsUser` aus einem Effect in
   * providers.tsx gesetzt, der erst im naechsten Render laeuft. `user_logged_in`
   * und `membership_signup_completed` waren deshalb in Production ausnahmslos
   * ohne `user_id` — 720 bzw. 338 Ereignisse.
   *
   * Ueberall sonst ungenutzt lassen: `_userId` ist dort bereits korrekt, und
   * eine manuelle ID koennte davon abweichen.
   */
  userId?: string | null;
};

// ---------------------------------------------------------------------------
// Google Analytics 4
// ---------------------------------------------------------------------------

/**
 * Consent-Stand fuer GA, gesetzt aus providers.tsx.
 *
 * Bewusst ein eigenes Gate statt "ist `window.gtag` da?": `window.dataLayer`
 * existiert durch dd360 auf jeder Seite, unabhaengig vom Consent. Ein Event,
 * das vor der Zustimmung dort landet, wuerde gtag.js beim spaeteren Laden
 * nachtraeglich abarbeiten und senden — das widerspricht dem Basic Mode.
 *
 * `unknown` heisst: Cookiebot hat noch nicht geantwortet. Events werden dann
 * kurz gepuffert und erst gesendet, wenn die Zustimmung feststeht.
 */
export type GaConsent = "unknown" | "granted" | "denied";

let _gaConsent: GaConsent = "unknown";
let _gaQueue: { name: string; params: Record<string, unknown>; at: number }[] = [];
let _gaFlushTimer: ReturnType<typeof setInterval> | null = null;

/**
 * So lange wartet ein Event nach erteiltem Consent auf gtag.js, danach
 * verfaellt es. Solange Cookiebot noch nicht beantwortet ist, verfaellt
 * nichts: Die Newsletter-Bestaetigung oeffnet oft in einem frischen Browser,
 * wo der Banner erst nach Sekunden beantwortet wird. Gesendet wird ohnehin
 * nur bei Zustimmung.
 */
const GA_QUEUE_TTL_MS = 10_000;
const GA_QUEUE_MAX = 20;

const isProduction = process.env.NEXT_PUBLIC_VERCEL_ENV === "production";

function deviceType() {
  // Dieselbe Weiche wie Header und AuthModalButton: darunter fuehrt
  // "Jetzt anmelden" auf /login statt ins Modal.
  return typeof window !== "undefined" && window.innerWidth < 1280
    ? "mobile"
    : "desktop";
}

function str(value: unknown) {
  return typeof value === "string" && value ? value : undefined;
}

/**
 * Welche internen Events an GA gehen, unter welchem Namen und mit welchen
 * Parametern. Nur was hier steht, verlaesst die Seite Richtung Google —
 * keine E-Mail-Adressen, Namen oder Freitexte.
 *
 * `null` aus der Funktion heisst: dieses Vorkommen nicht senden.
 */
const GA_EVENTS: Record<
  string,
  (p: Record<string, unknown>) => { name: string; params: Record<string, unknown> } | null
> = {
  cta_clicked: (p) => ({
    name: "cta_click",
    params: { cta_location: str(p.location), cta_label: str(p.label), device: deviceType() },
  }),
  cta_section_clicked: (p) => ({
    name: "cta_click",
    params: {
      cta_location:
        p.placement === "article_inline" ? "article_cta_section" : "content_cta_section",
      cta_label: str(p.label),
      device: deviceType(),
    },
  }),
  // Das Mobilmenue meldet jeden Link; GA will nur "Jetzt anmelden".
  mobile_menu_link_clicked: (p) =>
    p.href === "/login"
      ? {
          name: "cta_click",
          params: { cta_location: "mobile_menu", cta_label: str(p.label), device: "mobile" },
        }
      : null,
  event_signup: (p) => ({
    name: "event_signup",
    params: { event_type: str(p.event_type), member_only: p.member_only === true },
  }),
  membership_signup_completed: () => ({ name: "sign_up", params: {} }),
  newsletter_confirmed: (p) => ({
    name: "newsletter_confirmed",
    params: { topic: str(p.topic) },
  }),
};

function gtagReady() {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

function flushGaQueue() {
  if (_gaConsent === "denied") _gaQueue = [];
  if (_gaConsent === "granted") {
    if (gtagReady()) {
      for (const e of _gaQueue) window.gtag!("event", e.name, e.params);
      _gaQueue = [];
    } else {
      const now = Date.now();
      _gaQueue = _gaQueue.filter((e) => now - e.at < GA_QUEUE_TTL_MS);
    }
  }

  // Gepollt wird nur, um nach erteiltem Consent auf gtag.js zu warten. Den
  // Wechsel von `unknown` meldet setGaConsent selbst.
  const waiting = _gaConsent === "granted" && _gaQueue.length > 0;
  if (waiting && !_gaFlushTimer) {
    _gaFlushTimer = setInterval(flushGaQueue, 250);
  } else if (!waiting && _gaFlushTimer) {
    clearInterval(_gaFlushTimer);
    _gaFlushTimer = null;
  }
}

/**
 * Set from providers.tsx whenever Cookiebot's answer changes. `granted` only
 * when the visitor allowed statistics *and* a GA ID is configured.
 */
export function setGaConsent(consent: GaConsent) {
  // Die TTL zaehlt ab dem Consent, nicht ab dem Klick (siehe GA_QUEUE_TTL_MS).
  if (consent === "granted" && _gaConsent !== "granted") {
    const now = Date.now();
    for (const e of _gaQueue) e.at = now;
  }
  _gaConsent = consent;
  flushGaQueue();
}

function sendToGa(eventName: string, properties?: Record<string, unknown>) {
  if (typeof window === "undefined" || _gaConsent === "denied") return;

  const mapped = GA_EVENTS[eventName]?.(properties ?? {});
  if (!mapped) return;

  const params: Record<string, unknown> = { ...mapped.params };
  // Previews senden in dieselbe Property; der GA-Datenfilter
  // "Entwickler-Traffic" haelt sie damit aus den Berichten. Seitenaufrufe
  // bekommen das Flag ueber die `config` in providers.tsx.
  if (!isProduction) params.debug_mode = true;

  if (_gaConsent === "granted" && gtagReady()) {
    window.gtag!("event", mapped.name, params);
    return;
  }

  if (_gaQueue.length >= GA_QUEUE_MAX) return;
  _gaQueue.push({ name: mapped.name, params, at: Date.now() });
  flushGaQueue();
}

/**
 * Send an event to GA only, without PostHog and our DB.
 *
 * For events our DB already records elsewhere: `newsletter_confirmed` is
 * tracked server-side, but GA needs the browser's client ID.
 */
export function trackGaEvent(eventName: string, properties?: Record<string, unknown>) {
  sendToGa(eventName, properties);
}

/**
 * Track an event — sends to PostHog, our DB and, for events listed in
 * `GA_EVENTS`, Google Analytics.
 * Drop-in replacement for posthog.capture().
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>,
  options?: TrackOptions
) {
  // Send to PostHog (existing behavior). Ausserhalb der Produktion ist
  // PostHog nicht initialisiert — der Versand an die eigene DB unten laeuft
  // aber weiter, damit Staging-Tests in analytics_events sichtbar bleiben.
  //
  // `undefined` heisst "nicht uebergeben" und faellt auf `_userId` zurueck;
  // ein ausdrueckliches `null` bleibt `null`, statt still ueberschrieben zu
  // werden. Einmal aufgeloest, damit beide Senken unten denselben Wert sehen.
  const resolvedUserId =
    options && "userId" in options ? options.userId : _userId;

  // PostHog hat dieselbe Luecke wie die eigene DB: `posthog.identify` laeuft
  // ebenfalls erst im Effect in providers.tsx, das Ereignis haengt also noch
  // am anonymen Distinct-ID. Ein `identify` hier waere der sauberere Weg,
  // greift aber in PostHogs Identitaetsverwaltung ein — stattdessen reist die
  // ID als Property mit, womit sich das Ereignis dort nachtraeglich zuordnen
  // laesst.
  if (posthog.__loaded) {
    posthog.capture(
      eventName,
      resolvedUserId ? { ...properties, user_id: resolvedUserId } : properties,
    );
  }

  // Send to our DB (fire-and-forget)
  sendToApi("/api/analytics/track", {
    eventName,
    properties: properties ?? null,
    url: typeof window !== "undefined" ? window.location.href.split("#")[0] : null,
    userId: resolvedUserId,
  });

  sendToGa(eventName, properties);
}

/**
 * Log a client-side error to our DB (fire-and-forget).
 */
export function logError(
  error: unknown,
  metadata?: Record<string, unknown>
) {
  const message =
    error instanceof Error ? error.message : String(error);
  const stack =
    error instanceof Error ? error.stack ?? null : null;

  sendToApi("/api/analytics/error", {
    message,
    stack,
    url: typeof window !== "undefined" ? window.location.href : null,
    source: "client",
    userId: _userId,
    metadata: metadata ?? null,
  });
}

/**
 * Log a server-side error directly to the DB.
 * Use in API route catch blocks.
 */
export async function logServerError(
  error: unknown,
  context?: { userId?: string; url?: string; metadata?: Record<string, unknown> }
) {
  try {
    const { db } = await import("@/lib/db");
    const { errorLogs } = await import("@/lib/db/schema");

    await db.insert(errorLogs).values({
      userId: context?.userId ?? null,
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? (error.stack ?? null) : null,
      url: context?.url ?? null,
      source: "server" as const,
      metadata: context?.metadata ?? null,
    });
  } catch (err) {
    console.error("[logServerError] Failed to log:", err);
  }
}
